import { differenceInMilliseconds } from 'date-fns/differenceInMilliseconds'
import { isAfter } from 'date-fns/isAfter'
import { isBefore } from 'date-fns/isBefore'
import { isValid } from 'date-fns/isValid'
import { parseISO } from 'date-fns/parseISO'
import { startOfDay } from 'date-fns/startOfDay'

import {
  getServiceDateKey,
  parseServiceDate,
} from '../MenuHeader/ServiceSelectionGraphql/serviceWeeks'

export type MealKey = 'breakfast' | 'lunch' | 'dinner'
export type ServiceStatus = 'open' | 'placed' | 'closed' | 'noMenu' | 'openInFuture'

type ServiceLike =
  | {
      menuIsPublished?: boolean | null
      closeTime?: string | null
      openTime?: string | null
      orderedItems?: { orderPlaced?: boolean | null } | null
    }
  | null
  | undefined

function parseServiceTime(
  value: string | null | undefined,
  now: Date,
  timezone?: string | null,
): Date {
  if (value === undefined) return now
  return value === null
    ? new Date(NaN)
    : timezone
      ? parseServiceDate(value, timezone)
      : parseISO(value)
}

export function getServiceStatus(
  service: ServiceLike,
  mealKey: MealKey,
  now = new Date(),
  timezone?: string | null,
): ServiceStatus | null {
  if (!service) return null
  const closeTime = parseServiceTime(service.closeTime, now, timezone)
  // Dinner remains visible through the closing day at the account location.
  const isClosed =
    mealKey === 'dinner'
      ? timezone
        ? isValid(closeTime) &&
          (getServiceDateKey(closeTime, timezone) ?? '') < (getServiceDateKey(now, timezone) ?? '')
        : isBefore(startOfDay(closeTime), startOfDay(now))
      : isBefore(closeTime, now)
  if (isClosed) return 'closed'
  if (!service.menuIsPublished) return 'noMenu'
  if (service.orderedItems?.orderPlaced) return 'placed'
  if (isAfter(parseServiceTime(service.openTime, now, timezone), now)) return 'openInFuture'
  return 'open'
}

export function getServiceCell(
  service: ServiceLike,
  mealKey: MealKey,
  now = new Date(),
  timezone?: string | null,
) {
  const status = getServiceStatus(service, mealKey, now, timezone)
  const isDisabled =
    !service || status === 'closed' || status === 'noMenu' || status === 'openInFuture'
  let timeUntilMenuOpen: string | null = null
  if (status === 'openInFuture' && service?.openTime) {
    const remaining = differenceInMilliseconds(
      parseServiceTime(service.openTime, now, timezone),
      now,
    )
    if (remaining > 0) {
      const hours = Math.floor(remaining / 3_600_000)
      const minutes = Math.floor(remaining / 60_000) % 60
      timeUntilMenuOpen = `${hours > 0 ? `${hours}h ` : ''}${minutes}m`
    }
  }
  return { status, isDisabled, timeUntilMenuOpen }
}
